// DeepLinkPicker.jsx — route + params picker for the tap-action on a notification.
//
// Route params can declare a `dataSource: 'services' | 'providers'` — in that
// case the picker renders a searchable react-select backed by Redux state
// (state.providers.services / .provider) so marketing chooses from the actual
// live catalog rather than pasting an ID by hand. All other params fall back
// to a plain text input.
import React from 'react';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { FormGroup, Input, Label } from 'reactstrap';
import { ROUTE_CATALOG, getRoute } from '../helpers/routeCatalog.js';

const selectPortalProps = {
  classNamePrefix: 'rs',
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  styles: { menuPortal: base => ({ ...base, zIndex: 9999 }) },
};

// Renders the widget for a single route param — either a searchable Select
// (when dataSource is set) or a plain text Input (the fallback).
const ParamField = ({ param, value, onChange, services, providers }) => {
  const label = (
    <Label className="small mb-1">
      {param.label}
      {param.required && <span className="text-danger">{' *'}</span>}
    </Label>
  );

  // Auto-fill params — server resolves at fire time from the trigger context.
  // No point letting marketing type an ID that will be overwritten (or worse,
  // typed wrong and shipped to users).
  if (param.autoFill) {
    return (
      <FormGroup className="mb-2">
        {label}
        <div
          className="p-2 rounded"
          style={{ background: '#e9f5ff', border: '1px solid #b6d9f7', fontSize: '0.85rem' }}
        >
          <strong>Auto-filled at send time</strong>
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
            The server picks up the {param.label.toLowerCase()} from the event that
            triggered this automation (e.g. the completed booking). Falls back to
            the user's current active order if no context is available.
          </div>
        </div>
      </FormGroup>
    );
  }

  if (param.dataSource === 'services') {
    const opts = Object.values(services || {})
      .filter(s => s && s.name && s.active !== false)
      .map(s => ({ value: s.key || s.id, label: s.name }))
      .filter(o => o.value)
      .sort((a, b) => a.label.localeCompare(b.label));
    const selected = value ? (opts.find(o => o.value === value) || { value, label: value }) : null;
    return (
      <FormGroup className="mb-2">
        {label}
        <Select
          value={selected}
          onChange={(opt) => onChange(opt ? opt.value : '')}
          options={opts}
          placeholder={opts.length ? 'Select a service…' : '(no services in catalog)'}
          isClearable
          isDisabled={opts.length === 0}
          {...selectPortalProps}
        />
      </FormGroup>
    );
  }

  if (param.dataSource === 'providers') {
    const opts = Object.values(providers || {})
      .map(p => ({ value: p.key || p.id, label: p.name || '(unnamed)' }))
      .filter(o => o.value)
      .sort((a, b) => a.label.localeCompare(b.label));
    const selected = value ? (opts.find(o => o.value === value) || { value, label: value }) : null;
    return (
      <FormGroup className="mb-2">
        {label}
        <Select
          value={selected}
          onChange={(opt) => onChange(opt ? opt.value : '')}
          options={opts}
          placeholder={opts.length ? 'Select a service provider…' : '(no providers loaded)'}
          isClearable
          isDisabled={opts.length === 0}
          {...selectPortalProps}
        />
      </FormGroup>
    );
  }

  return (
    <FormGroup className="mb-2">
      {label}
      <Input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`${param.label}${param.required ? ' *' : ''}`}
      />
    </FormGroup>
  );
};

// `context` — either 'campaign' or 'automation'. Filters ROUTE_CATALOG so
// per-user routes (booking, rate) don't appear in the campaign composer.
// Defaults to 'automation' (permissive) for backwards compatibility.
const DeepLinkPicker = ({ value, onChange, context = 'automation' }) => {
  const services = useSelector(s => s.providers?.services);
  const providers = useSelector(s => s.providers?.provider);

  const deepLink = value || { route: 'home', params: {} };
  const route = getRoute(deepLink.route);

  const setRoute = (newRoute) => onChange({ route: newRoute, params: {} });
  const setParam = (k, v) => onChange({ ...deepLink, params: { ...deepLink.params, [k]: v } });

  const opts = ROUTE_CATALOG
    .filter(r => !r.contexts || r.contexts.includes(context))
    .map(r => ({ value: r.value, label: r.label }));
  const selected = route ? { value: route.value, label: route.label } : null;

  return (
    <>
      <FormGroup>
        <Label>Tap destination</Label>
        <Select
          value={selected}
          onChange={(opt) => setRoute(opt ? opt.value : 'home')}
          options={opts}
          {...selectPortalProps}
        />
      </FormGroup>
      {route && route.params.length > 0 && (
        <FormGroup>
          <Label className="small">Parameters</Label>
          {route.params.map(p => (
            <ParamField
              key={p.key}
              param={p}
              value={deepLink.params?.[p.key]}
              onChange={(v) => setParam(p.key, v)}
              services={services}
              providers={providers}
            />
          ))}
        </FormGroup>
      )}
    </>
  );
};

export default DeepLinkPicker;
